import React, { useState } from "react"
import { Alert, Button, Modal } from "antd"
import { CheckCircleFilled, CheckCircleOutlined } from "@ant-design/icons"
import Mod from "react-bootstrap/Modal"
import { Spinner } from "react-bootstrap"
import Details from "../Application/Details"
import "../Application/MyRates/MyRates.css"
import * as com from "../Common"
import { selectSelectedRateV2 } from "./api/borrowerSideV2Client"
import { hydrateReduxFromCurrentState } from "../Store"
import { track } from "../statsig"
import { STATSIG } from "../statsig/events"

const emptySelectedLoan = { hash: "" }

const RatesV2 = ({ application, onApplicationUpdated }) => {
    const selection = application?.selection || {}
    const loSelectedLoans = Array.isArray(selection.loSelectedLoans) ? selection.loSelectedLoans : []
    const selectedLoan = selection.selectedloan || emptySelectedLoan
    const property = application?.property || {}

    const [notifyingLO, setNotifyingLO] = useState(false)
    const [remindingLO, setRemindingLO] = useState(false)
    const [choosingRate, setChoosingRate] = useState(false)
    const [showReminderSentModal, setShowReminderSentModal] = useState(false)
    const [show, setShow] = useState(false)
    const [detailedCard, setDetailedCard] = useState(null)
    const [isExporting, setIsExporting] = useState(false)

    const contactLO = () => {
        setRemindingLO(true)
        const token = sessionStorage.getItem("ZeitroA")
        fetch("/borrower/notifylo", {
            method: "POST",
            body: JSON.stringify({ notification: "productselectionstarted" }),
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
                Cache: "no-cache",
            },
        }).then((response) => {
            if (response.status !== 200) {
                console.log("Looks like there was a problem. Status Code: " + response.status)
                return
            }
            setRemindingLO(false)
            setShowReminderSentModal(true)
        }).catch((err) => {
            setRemindingLO(false)
            console.log("Fetch Error :", err)
        })
    }

    const showAlert = () => {
        if (selectedLoan.hash) {
            return (
                <div className="ratesTips d-flex mt-2">
                    <div>
                        <img src="/images/checkEmail.png" alt="checkEmail" style={{ height: 18, width: 21, marginRight: 10 }} />
                    </div>
                    <div className="ml-2">
                        <div className="fw-500">You've selected a rate!</div>
                        <div className="mt-1">Your loan officer will review your application and get in touch about what comes next.</div>
                    </div>
                </div>
            )
        }
        if (loSelectedLoans.length === 0) {
            return (
                <div className="ratesTips d-flex mt-2">
                    <div>
                        <img src="/images/maganifier.png" alt="maganifier" style={{ width: 44, height: 42 }} />
                    </div>
                    <div className="ml-2">
                        <div className="fw-500 text-wrap">Your officer is working on your rates</div>
                        <div className="mt-1 text-wrap">Your Loan Officer is reviewing your documents and you will be notified by email when this page is updated with your rate.</div>
                        <Button type="primary" className="mt-3" loading={remindingLO} onClick={() => contactLO()}>Remind my officer</Button>
                    </div>
                </div>
            )
        }
        return (
            <div className="taskTips">
                <Alert
                    message={
                        <div className="rate-num-tips">
                            <img src="/images/checkEmail.png" alt="checkEmail" />
                            <div>Your loan officer has chosen {loSelectedLoans.length} rates for you!</div>
                        </div>
                    }
                    className="mt-2"
                    type="info"
                    closable
                />
            </div>
        )
    }

    const chooseRate = async (loan) => {
        if (!loan?.hash || choosingRate) {
            return
        }
        setChoosingRate(true)
        try {
            const body = await selectSelectedRateV2(loan.hash)
            if (body.Application && onApplicationUpdated) {
                onApplicationUpdated(body.Application)
            }
            // Mirror borrower-selected rate into Redux for Details / other legacy readers.
            hydrateReduxFromCurrentState()
            track(STATSIG.LOAN.RATE_SELECTION, "done", {
                type: "done",
                loan_id: application?.loanId || "",
                hash: loan.hash || "",
            })
            notifyLO(loan)
        } catch (err) {
            console.error(err)
            alert(err.message || "Failed to select rate")
        } finally {
            setChoosingRate(false)
        }
    }

    const notifyLO = (rate) => {
        setNotifyingLO(true)
        const token = sessionStorage.getItem("ZeitroA")
        fetch("/borrower/notifyloaboutchosenrate", {
            method: "POST",
            body: JSON.stringify({
                base_rate: String(rate.base_rate),
                apr: String(rate.APR),
                monthly_payment: String(rate.monthly_payment),
                closing_cost: String(rate.closing_cost),
                points: String(rate.points),
                amortization: String(rate.amortization),
                term: String(rate.Term),
                mtype: String(rate.Mtype),
                loansize: String(rate.loansize),
                downpayment: String(rate.downpayment),
            }),
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
                Cache: "no-cache",
            },
        }).then((response) => {
            if (response.status !== 200) {
                console.log("Looks like there was a problem. Status Code: " + response.status)
                setNotifyingLO(false)
                return
            }
            setNotifyingLO(false)
            alert("Your loan officer has been notified! They will be in contact with you shortly to discuss your application and provide further guidance.")
        }).catch((err) => {
            setNotifyingLO(false)
            console.log(err)
        })
    }

    const getLTV = (rate) => {
        const { salesprice, appraisal } = property
        const loanbalance = application?.loanbalance
        const purpose = rate.purpose || property.purpose
        if (purpose === "purchase") {
            return Math.round(100 * (parseFloat(salesprice) - parseFloat(rate.downpayment)) / parseFloat(salesprice))
        }
        if (purpose === "cashoutrefinance") {
            return Math.round(100 * (com.safeParseInt(loanbalance) + com.safeParseInt(rate.cashout)) / parseFloat(appraisal))
        }
        return Math.round(100 * (com.safeParseInt(loanbalance) - com.safeParseInt(rate.paydown)) / parseFloat(appraisal))
    }

    const displayLoSelectedRateCard = (rate, i) => {
        const isSelected = rate.hash === selectedLoan.hash
        const item = rate.matchingproducts?.[0]
        const program = item
            ? `${com.divideStringByCapitalLetter(item.Lender)}, ${item.Name}`
            : com.capitalize(String(rate.Mtype || "").replace("_", " ").toLowerCase().replace("fha", "FHA"))

        const getDownpayment = () => {
            if (rate.purpose === "purchase") {
                return "$" + com.commaize(rate.downpayment) + "/" + getLTV(rate) + "%"
            }
            if (rate.purpose === "cashoutrefinance") {
                return "$" + com.commaize(rate.cashout) + "/" + getLTV(rate) + "%"
            }
            return "$" + com.commaize(rate.paydown) + "/" + getLTV(rate) + "%"
        }

        const getDownpaymentTitle = () => {
            if (rate.purpose === "purchase") {
                return "Down payment/LTV"
            }
            if (rate.purpose === "cashoutrefinance") {
                return "Cash out/LTV"
            }
            return "Paydown principal/LTV"
        }

        const getAmortization = (value) => {
            switch (value) {
                case "arm56":
                    return "ARM 5/6"
                case "arm76":
                    return "ARM 7/6"
                case "arm106":
                    return "ARM 10/6"
                default:
                    return "Fixed"
            }
        }

        return (
            <div className="loSelectedRateCardWrap" key={rate.hash || i}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <div style={{ padding: 4, backgroundColor: "#F5F5F7", borderRadius: 4 }}>
                            <img src="/images/rates.png" alt="rates" style={{ width: 25, height: 25 }} />
                        </div>
                        <div className="fs-16 rateItemTitle">My Rate {i + 1}</div>
                    </div>
                    {isSelected ? (
                        <div className="selected-rate-button d-flex">
                            <div className="chosenRateIcon">
                                <CheckCircleFilled />
                            </div>
                            <Button loading={notifyingLO} className="chosenRateButton" onClick={() => notifyLO(rate)}>
                                Confirm Selection
                            </Button>
                        </div>
                    ) : (
                        <Button className="selected-rate-button unchosenRateButton" loading={choosingRate} onClick={() => chooseRate(rate)}>
                            <CheckCircleOutlined />
                            <div style={{ color: "black", marginLeft: 5 }} className="fw-500">Choose rate</div>
                        </Button>
                    )}
                </div>
                <div className="fs-16 fw-500 rateItemInfoTitle">{program}</div>
                <div className="my-rates-card d-flex justify-content-between">
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">Term, type</div>
                        <div className="fw-400 fs-16 rateItemInfoValue">{rate.Term}, {getAmortization(rate.amortization)}</div>
                    </div>
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">Loan size</div>
                        <div className="fw-400 fs-16 rateItemInfoValue">${com.commaize(rate.loansize)}</div>
                    </div>
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">{getDownpaymentTitle()}</div>
                        <div className="fw-400 fs-16 rateItemInfoValue">{getDownpayment()}</div>
                    </div>
                </div>
                <div className="my-rates-card-grey d-flex justify-content-between greyBackground">
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">Rate/APR</div>
                        <div className="my-rates-card-grey-value">{rate.base_rate}%/{rate.APR}%</div>
                    </div>
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">Mo. payment</div>
                        <div className="my-rates-card-grey-value">${com.commaize(rate.monthly_payment)}</div>
                    </div>
                    <div>
                        <div className="title-5 fw-500 d-flex align-items-center rateItemInfoLabel" style={{ gap: 4 }}>
                            <div>Closing cost</div>
                            <img
                                src="/images/rate-detailedCard.svg"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setDetailedCard(rate)
                                    setShow(true)
                                }}
                                alt="details"
                            />
                        </div>
                        <div className="my-rates-card-grey-value">${com.commaize(rate.closing_cost)}</div>
                    </div>
                    <div>
                        <div className="title-5 fw-500 rateItemInfoLabel">Points</div>
                        <div className="my-rates-card-grey-value">{rate.points}%</div>
                    </div>
                </div>
                <div className="selected-rate-button-xs-wrap">
                    {isSelected ? (
                        <div className="d-flex">
                            <div className="chosenRateIcon">
                                <CheckCircleFilled />
                            </div>
                            <Button loading={notifyingLO} className="chosenRateButton" onClick={() => notifyLO(rate)}>
                                Confirm Selection
                            </Button>
                        </div>
                    ) : (
                        <Button className="unchosenRateButton" loading={choosingRate} onClick={() => chooseRate(rate)}>
                            <CheckCircleOutlined />
                            <div className="fw-500" style={{ color: "black", marginLeft: 5 }}>Choose rate</div>
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    const exportPDF = () => {
        setIsExporting(true)
        import("jspdf").then(({ default: jsPDF }) => {
            import("html2canvas").then(({ default: html2canvas }) => {
                const pdf = new jsPDF("p", "mm", "a4")
                const detailsComponent = document.querySelector(".loandetails .modal-body")
                if (!detailsComponent) {
                    console.error("Could not find Details component")
                    setIsExporting(false)
                    return
                }
                const tabLinks = detailsComponent.parentElement.querySelectorAll(".nav-link")
                tabLinks[0].click()
                setTimeout(() => {
                    const closingCostDetails = detailsComponent.querySelector(".tab-pane.active")
                    html2canvas(closingCostDetails).then((canvas) => {
                        const imgData = canvas.toDataURL("image/png")
                        const imgWidth = 210
                        const imgHeight = canvas.height * imgWidth / canvas.width
                        pdf.setFontSize(18)
                        pdf.setFont(undefined, "bold")
                        pdf.text("Closing Cost Details", 105, 20, { align: "center" })
                        pdf.addImage(imgData, "PNG", 0, 30, imgWidth, imgHeight)
                        pdf.addPage()
                        pdf.setFontSize(18)
                        pdf.setFont(undefined, "bold")
                        pdf.text("Loan Overview", 105, 20, { align: "center" })
                        tabLinks[1].click()
                        setTimeout(() => {
                            const overviewTab = detailsComponent.querySelector(".tab-pane.active")
                            html2canvas(overviewTab).then((canvas2) => {
                                const imgData2 = canvas2.toDataURL("image/png")
                                const imgHeight2 = canvas2.height * imgWidth / canvas2.width
                                pdf.addImage(imgData2, "PNG", 0, 30, imgWidth, imgHeight2)
                                pdf.save("Estimated Fee Sheet.pdf")
                                setIsExporting(false)
                            }).catch((err) => {
                                console.error("Error generating PDF:", err)
                                setIsExporting(false)
                            })
                        }, 300)
                    }).catch((err) => {
                        console.error("Error generating PDF:", err)
                        setIsExporting(false)
                    })
                }, 300)
            }).catch((err) => {
                console.error("Error importing html2canvas:", err)
                setIsExporting(false)
            })
        }).catch((err) => {
            console.error("Error importing jsPDF:", err)
            setIsExporting(false)
        })
    }

    return (
        <div className="text-left newInterviewPaneEmployment">
            <div className="title-4">My Rates</div>
            {showAlert()}
            <div className="mt-3">
                {loSelectedLoans.map((rate, i) => displayLoSelectedRateCard(rate, i))}
            </div>
            <Modal
                title="Reminder sent"
                open={showReminderSentModal}
                onCancel={() => setShowReminderSentModal(false)}
                footer={[
                    <Button key="back" type="primary" onClick={() => setShowReminderSentModal(false)}>
                        OK
                    </Button>,
                ]}
            >
                <p>Your loan officer has received a reminder to choose your rates. You will receive an email notification once your rates have been selected.</p>
            </Modal>
            <Mod show={show} dialogClassName="loandetails mt-5" onHide={() => setShow(false)}>
                <Mod.Header closeButton>
                    <Mod.Title className="d-flex justify-content-between w-100">
                        <div style={{ fontSize: 24, fontWeight: "bold", color: "white" }}>Estimated Fee Sheet</div>
                        <div className="d-flex align-items-center">
                            <Button variant="secondary" onClick={exportPDF} className="mr-auto">
                                {isExporting ? <Spinner animation="border" size="sm" /> : "Export"}
                            </Button>
                        </div>
                    </Mod.Title>
                </Mod.Header>
                <Mod.Body>
                    <Details seletion={selectedLoan} card={detailedCard} />
                </Mod.Body>
                <Mod.Footer>
                    <Button variant="primary" onClick={() => setShow(false)}>
                        Close
                    </Button>
                </Mod.Footer>
            </Mod>
        </div>
    )
}

export default RatesV2
